import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import * as Sentry from '@sentry/react';
import { HashRouter as Router } from 'react-router-dom';
import Layout from '../../components/Layout';
import api from '../../api/req';
import { AppContext } from '../../providers/authProvider';
import { LogicaContext } from '../../providers/authLogicaProvider';
import LoginForm from '../../components/Login';
import MuiTheme from './muiTheme';
import WinmgrRoot from './winMgr';

function Root() {
  const [currentUser, setCurrentUser] = useState(null);
  const tokens = useRef({
    accessToken: null,
    refreshToken: null,
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // key-value store for all settings in current session
  const [settings, setSettings] = useState(null);
  const [ticket, setTicket] = useState(() => localStorage.getItem('ticket'));

  const clearSession = useCallback(
    () => {
      window.localStorage.removeItem('refreshToken');
      window.localStorage.removeItem('ticket');
      setCurrentUser(null);
      tokens.current.accessToken = null;
      tokens.current.refreshToken = null;
      setTicket(null);
    },
    [tokens],
  );

  const tryCurrentUser = useCallback(
    async (accessToken, refreshToken) => {
      const resp = await api.get$('/api/core/currentuser/', () => ({ access: accessToken }));
      if (resp.ok) {
        const d = await resp.json();
        setCurrentUser(d.results[0]);
        setLoading(false);
        setErr(null);
        tokens.current.accessToken = accessToken;
        tokens.current.refreshToken = refreshToken;

        if (process.env.NODE_ENV === 'production') {
          Sentry.setUser(d.results[0]);
        }

        return true;
      // () => ({
      //   access: accessToken,
      //   onRefresh: tryRefresh,
      // });
      }
      clearSession();
      setLoading(false);
      setErr(`${resp.status} ${resp.statusText}`);
      return false;
    },
    [clearSession, tokens],
  );

  const tryRefresh = useCallback(
    async () => {
    // eslint-disable-next-line
    const refreshToken = tokens.current.refreshToken || window.localStorage.getItem('refreshToken');
      if (refreshToken) {
        const r = await api.post$('/api/token/refresh', () => null, { refresh: refreshToken });
        if (r.ok) {
          const d = await r.json();
          window.localStorage.setItem('refreshToken', refreshToken);
          tokens.current.refreshToken = refreshToken;
          const result = await tryCurrentUser(d.access, refreshToken);
          if (result) {
            return result;
          }
          window.localStorage.removeItem('refreshToken');
        }
      }
      return false;
    },
    [tokens, tryCurrentUser],
  );

  const auth = useMemo(
    () => ({
      access: tokens.current.accessToken,
      onRefresh: tryRefresh,
    }),
    [tokens, tryRefresh],
  );

  const tryLogout = useCallback(
    () => {
      setLoading(true);
      api.get$('/api-auth/logout', () => auth)
        .then((r) => {
          if (r.ok) {
            clearSession();
            setLoading(false);
            setErr(null);
          } else {
            setLoading(false);
            setErr(`${r.status} ${r.statusText}`);
          }
        });
    },
    [auth, clearSession],
  );

  const tryLogin = useCallback(
    async (username, password) => {
      setLoading(true);
      const r = await api.post$('/api/token/', () => null, {
        username,
        password,
      });
      if (r.ok) {
        const d = await r.json();
        if (await tryCurrentUser(d.access, d.refresh)) {
          window.localStorage.setItem('refreshToken', d.refresh);
        }
      } else {
        try {
          const d = await r.json();
          const errors = Object.keys(d)
            .reduce((R, k) => [...R, ...d[k]], []);
          const errorMsg = errors.reduce((R, _err) => `${R} ${_err}`, '');
          clearSession();
          setLoading(false);
          setErr(errorMsg);
        } catch {
          clearSession();
          setLoading(false);
          setErr(`${r.status} ${r.statusText}`);
        }
      }
    },
    [clearSession, tryCurrentUser],
  );

  const onSetSettings = useCallback(
    (key, value) => {
      setSettings((o) => ({ ...o, [key]: value }));
    },
    [],
  );

  const getAuth = useCallback(
    () => ({
      access: tokens.current.accessToken,
      onRefresh: tryRefresh,
    }),
    [tokens, tryRefresh],
  );

  const onSetTicket = useCallback(
    (tkt) => {
      if (tkt) {
        setTicket(tkt);
        localStorage.setItem('ticket', tkt);
      } else {
        setTicket(null);
        localStorage.removeItem('ticket');
      }
    },
    [],
  );

  useEffect(() => {
    setLoading(true);
    tryRefresh().then(
      () => setLoading(false),
    );
  }, [tryRefresh]);

  const ctxValue = useMemo(
    () => ({
      currentUser,
      accessToken: tokens.current.accessToken,
      refreshToken: tokens.current.refreshToken,
      logoutHandler: tryLogout,
      refreshHandler: tryRefresh,
      setSettings: onSetSettings,
      settings,
      auth: getAuth,
    }),
    [currentUser, getAuth, onSetSettings, settings, tokens, tryLogout, tryRefresh],
  );
  const logicaCtxValue = useMemo(
    () => ({
      ticket,
      setTicket: onSetTicket,
    }),
    [onSetTicket, ticket],
  );
  return (
    <Router>
      <MuiTheme>
        {!currentUser ? (
          <LoginForm onLogin={tryLogin} errorMsg={err} loading={loading} />
        ) : (
          <AppContext.Provider value={ctxValue}>
            <LogicaContext.Provider value={logicaCtxValue}>
              <WinmgrRoot>
                <Layout />
              </WinmgrRoot>
            </LogicaContext.Provider>
          </AppContext.Provider>
        )}
      </MuiTheme>
    </Router>
  );
}

export default Root;
